import type { FC } from 'react';
import {
  ACTION_VARIANTS,
  Button,
  Checkbox,
  ICON_NAMES,
  INLINE_ALERT_VARIANTS,
  InlineAlert,
} from '@reverse-hr/design-system';
import { Trans, useTranslation } from 'react-i18next';
import {
  FORM_DEFAULT_TOUCHED,
  FORM_DEFAULT_VALUES,
} from '@components/ShareLinkModal/_constants/ShareLinkModal.constants';
import { useShareLinkModalForm } from '@components/ShareLinkModal/_hooks/use-share-link-modal-form';
import { useStoreActions, useStoreState } from '@app/store/hooks/index';
import {
  jobClientSelector,
  postJobClientSelector,
} from '@model/Job/Job.selectors';
import { useParams } from 'react-router-dom';
import { useState } from 'react';
import type { TShareLinkModalFormProps } from '@components/ShareLinkModal/ShareLinkModal.types';
import { selectorUser } from '@model/User';
import { checkEmailDomainMismatch } from '@components/ShareLinkModal/_helpers/check-email-domain';
import { useUserPackageVisibility } from '@components/ShareLinkModal/_hooks/use-user-package-visibility';
import { ShareLinkModalFormFields } from '@components/ShareLinkModal/_partials/ShareLinkModalFormFields';
import { USER_TYPES_MAP } from '@app/constants/session';

export const ShareLinkModalForm: FC<TShareLinkModalFormProps> = props => {
  const { setAlertDetail } = props;
  const postClient = useStoreActions(postJobClientSelector);
  const jobMainClient = useStoreState(jobClientSelector);
  const { jobId, entityId } = useParams<{ jobId: string; entityId: string }>();
  const [isCompensationPackageVisible, setIsCompensationPackageVisible] =
    useState(false);

  const currentUser = useStoreState(selectorUser);
  const isMainClient = jobMainClient?.id === currentUser.id;
  const userPackageVisibility = useUserPackageVisibility();

  const { t } = useTranslation('', {
    keyPrefix: 'modal.share',
  });

  const { t: tCommon } = useTranslation('', {
    keyPrefix: 'common',
  });

  const mainClientFormattedName = tCommon('formatting.fullName', {
    firstName: jobMainClient?.firstName,
    lastName: jobMainClient?.lastName,
  });

  const {
    formData,
    onChange,
    isSubmitting,
    isFormValid,
    validationErrors,
    setIsSubmitting,
    setFormData,
    setTouched,
  } = useShareLinkModalForm();

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    const jobIdToUse = entityId || jobId;

    if (isFormValid && !isSubmitting && jobIdToUse) {
      setIsSubmitting(true);

      const { language, ...rest } = formData;

      try {
        await postClient({
          jobId: jobIdToUse,
          clientLanguage: language,
          isCompensationPackageVisible,
          ...rest,
        });

        setAlertDetail({
          visible: true,
          type: 'positive',
          icon: 'icn-check',
          children: t('alert.positive'),
        });

        setFormData(FORM_DEFAULT_VALUES);
        setTouched(FORM_DEFAULT_TOUCHED);
      } catch (error) {
        setAlertDetail({
          visible: true,
          type: 'negative',
          icon: 'icn-close-big',
          children: t('alert.negative'),
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const getEmailHint = (): string => {
    if (!formData.clientEmail || validationErrors.clientEmail) {
      return '';
    }

    return checkEmailDomainMismatch(
      formData.clientEmail,
      currentUser.email.split('@')[1],
    )
      ? t('inputs.email.hint')
      : '';
  };

  return (
    <div className="rev-o-container rev-o-container--full">
      <form onSubmit={handleSubmit} className="rev-o-grid">
        <ShareLinkModalFormFields
          validationErrors={validationErrors}
          formData={formData}
          onChange={onChange}
          getEmailHint={getEmailHint}
        />
        {isMainClient || currentUser.type === USER_TYPES_MAP.MANAGER ? (
          <div className="rev-u-px-16 rev-u-py-8 rev-o-col-12 rev-o-col--medium-12">
            <div className="c-share-link-modal__checkbox-alert">
              <Checkbox
                name="isCompensationPackageVisible"
                id="isCompensationPackageVisible"
                onChange={event =>
                  setIsCompensationPackageVisible(event.isChecked)
                }
              />
              <p>
                <Trans
                  i18nKey="modal.share.isCompensationPackageVisible.label"
                  components={{
                    strong: <strong />,
                  }}
                />
              </p>
            </div>
          </div>
        ) : null}
        {userPackageVisibility.isVisible &&
          !userPackageVisibility.isEditable && (
            <div className="rev-u-px-16 rev-u-py-8 rev-o-col-12 rev-o-col--medium-12">
              <InlineAlert
                variant={INLINE_ALERT_VARIANTS.INFO}
                icon={ICON_NAMES.INFO_CIRCLE_OUTLINE}
              >
                {t('alert.guest_info', {
                  mainClient: mainClientFormattedName,
                })}
              </InlineAlert>
            </div>
          )}
        <div className="rev-o-col-12 rev-u-px-16 rev-u-py-8">
          <Button
            type="submit"
            variant={ACTION_VARIANTS.PRIMARY}
            rightIcon={ICON_NAMES.SEND}
            disabled={!isFormValid || isSubmitting}
            isDisabled={!isFormValid || isSubmitting}
          >
            {t('button')}
          </Button>
        </div>
      </form>
    </div>
  );
};
